var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useState, useRef } from 'react';
import classnames from 'classnames';
import { BodyText } from '../../BodyText';
import { TagGroup } from '../../TagGroup';
import { Tooltip } from '../../Tooltip';
import { Button } from '../../Button';
import { Stack } from '../../Stack';
import { Icon } from '../../Icon';
import { Tag } from '../../Tag';
import { FormFieldContext } from '../../FormField';
import { keys } from '../../../utilities/keyCodes';
import { FocusContext } from '../utils';
import { useWindowWidth } from '@react-hook/window-size/throttled';
export const Trigger = (_a) => {
    var { className, disabled, el: TriggerEl = 'div', error, onClearClick, onClick, onClose, onBlur, onKeyDown, open, placeholder, value, shortLabel, shortLabelPosition = 'left', size = 'medium', tabIndex = 0, rows = 1, clearable = true, content } = _a, props = __rest(_a, ["className", "disabled", "el", "error", "onClearClick", "onClick", "onClose", "onBlur", "onKeyDown", "open", "placeholder", "value", "shortLabel", "shortLabelPosition", "size", "tabIndex", "rows", "clearable", "content"]);
    const { focusState } = useContext(FocusContext);
    const { triggerFocus } = focusState;
    const triggerRef = useRef(null);
    const tagArrRef = useRef([]);
    const { labelFor: inputId, setFormFieldContext } = useContext(FormFieldContext);
    React.useEffect(() => {
        setFormFieldContext({ inputRef: triggerRef });
        return () => setFormFieldContext({ inputRef: undefined });
    }, []); // eslint-disable-line
    React.useEffect(() => {
        var _a;
        triggerFocus && ((_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus());
    }, [triggerFocus, triggerRef]);
    const [tagLineCount, setTagLineCount] = useState(0);
    const [tagLineCutoffCount, setTagLineCutoffCount] = useState(0);
    const [widthCutoff, setWidthCutoff] = useState(0);
    const windowWidth = useWindowWidth({ fps: 60 });
    React.useLayoutEffect(() => {
        const tagTopSet = new Set();
        if (value) {
            for (let i = 0; i < value.length; i++) {
                const tag = tagArrRef.current[i];
                const tagTop = Math.ceil(tag === null || tag === void 0 ? void 0 : tag.getBoundingClientRect().top);
                if (isNaN(tagTop))
                    return;
                if (!tagTopSet.has(tagTop)) {
                    tagTopSet.add(tagTop);
                    if (tagTopSet.size === rows + 1) {
                        setTagLineCutoffCount(i);
                        setWidthCutoff(windowWidth);
                    }
                }
            }
        }
        setTagLineCount(tagTopSet.size);
    }, [value, rows, windowWidth]);
    const TriggerClasses = classnames('Trigger', className, {
        'Trigger--open': open,
        'Trigger--error': error,
        'Trigger--disabled': disabled,
        [`Trigger--s-${size}`]: size,
    });
    const handleClick = (e) => {
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    const preventClick = (e) => e.stopPropagation();
    const clearClick = (e) => {
        e.stopPropagation();
        onClearClick(e);
    };
    const handleKey = (e) => {
        if (onKeyDown)
            onKeyDown(e);
        if (e.key === keys.tab)
            return e; // Skip tabbing
        if (e.target !== triggerRef.current)
            return e; // Trigger isn't in focus
        if (e.key === keys.enter ||
            e.keyCode === 32 ||
            (e.key === keys.down && !open) ||
            (e.key === keys.up && open)) {
            e.preventDefault();
            handleClick(e);
        }
    };
    const shortLabelClasses = classnames('Trigger__shortLabel', className, {
        'Trigger__shortLabel--left': shortLabelPosition === 'left',
        'Trigger__shortLabel--right': shortLabelPosition === 'right',
    });
    const getShortLabel = () => {
        if (shortLabel === undefined || shortLabel === '')
            return;
        return React.createElement("div", { className: shortLabelClasses }, shortLabel);
    };
    const getValues = () => {
        if (content) {
            return (React.createElement(BodyText, { className: "Trigger__value" }, content(value)));
        }
        if (!value || value.length === 0) {
            return (React.createElement(BodyText, { className: "Trigger__placeholder" }, placeholder));
        }
        if (Array.isArray(value)) {
            if (rows === 0 ||
                (rows > 0 &&
                    tagLineCount > rows &&
                    tagLineCutoffCount < value.length &&
                    widthCutoff >= windowWidth)) {
                return (React.createElement(BodyText, { className: "Trigger__value" },
                    value.length,
                    " selected"));
            }
            tagArrRef.current = new Array(value.length);
            return (React.createElement(TagGroup, null, value.map((singleValue, index) => {
                const tagText = singleValue.text
                    ? singleValue.text
                    : typeof singleValue.content === 'string'
                        ? singleValue.content
                        : String(singleValue.value);
                const handleOnClose = () => onClose(singleValue);
                return (React.createElement(Tag, { key: index, onClose: !singleValue.disabled &&
                        onClose &&
                        !disabled &&
                        handleOnClose, onClick: preventClick, color: singleValue.color, tagRef: (el) => (tagArrRef.current[index] = el) }, tagText));
            })));
        }
        const text = value.text
            ? value.text
            : typeof value.content === 'string'
                ? value.content
                : String(value.value);
        return React.createElement(BodyText, { className: "Trigger__value" }, text);
    };
    const checkIfAllDisabled = () => {
        if (value &&
            value.length > 0 &&
            value.filter((item) => item.disabled)
                .length === value.length)
            return true;
    };
    const getClearable = () => {
        if (!clearable ||
            !value ||
            value.length === 0 ||
            Object.entries(value).length === 0 ||
            checkIfAllDisabled())
            return;
        return (React.createElement(Tooltip, { text: disabled ? null : 'Clear selection', className: "d-f" },
            React.createElement(Button, { disabled: disabled, xsmall: true, fill: "subtle", iconName: "clear", onClick: clearClick, className: "Trigger__clear", "aria-label": "Clear selection" })));
    };
    const getArrow = () => {
        if (open)
            return 'keyboard_arrow_up';
        return 'keyboard_arrow_down';
    };
    return (React.createElement(TriggerEl, Object.assign({ className: TriggerClasses, onClick: disabled ? undefined : handleClick, tabIndex: disabled ? undefined : tabIndex, onKeyPress: handleKey, onKeyDown: handleKey, ref: triggerRef, onBlur: onBlur, id: inputId }, props),
        React.createElement(Stack, null,
            shortLabelPosition === 'left' && getShortLabel(),
            getValues(),
            React.createElement(Stack, { className: "Trigger__controls", spacing: 1, alignItems: "flex-start" },
                getClearable(),
                React.createElement(Icon, { name: getArrow(), size: "16px", className: "Trigger__arrow" })),
            shortLabelPosition === 'right' && getShortLabel())));
};
//# sourceMappingURL=Trigger.js.map